import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { formatDate } from '../lib/utils';
import { STATUS } from '../lib/theme';
import { AlertCircle, Info, AlertTriangle, XCircle, Clock } from 'lucide-react';

const getLevelIcon = (level) => {
  switch (level) {
    case 'ERROR':
    case 'CRITICAL':
      return <XCircle className="h-4 w-4" />;
    case 'WARNING':
      return <AlertTriangle className="h-4 w-4" />;
    case 'INFO':
      return <Info className="h-4 w-4" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
};

const getLevelStatus = (level) => {
  switch (level) {
    case 'ERROR':
    case 'CRITICAL':
      return STATUS.critical;
    case 'WARNING':
      return STATUS.warning;
    case 'INFO':
      return STATUS.primary;
    default:
      return STATUS.expired;
  }
};

export function LogsTable({ logs }) {
  // Render Mobile Card View
  const renderMobileCard = (log) => {
    const colors = getLevelStatus(log.level);
    return (
      <Card key={log.id} className="mb-2" style={{ background: colors.bg, borderColor: colors.border }}>
        <CardContent className="p-3 space-y-2">
          {/* Header - Level & Time */}
          <div className="flex items-start justify-between gap-3">
            <Badge
              className="flex items-center gap-1.5 px-2 py-0.5"
              style={{ background: colors.bg, borderColor: colors.border, color: colors.text }}
            >
              {getLevelIcon(log.level)}
              <span className="font-medium">{log.level}</span>
            </Badge>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>{formatDate(log.timestamp)}</span>
            </div>
          </div>

          {/* Message */}
          <div className="text-xs leading-relaxed break-words">
            {log.message}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      {/* Desktop Table View - Hidden on mobile */}
      <div className="hidden md:block rounded-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Waktu</TableHead>
              <TableHead className="w-[120px]">Tingkat</TableHead>
              <TableHead>Pesan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  Tidak ada log aktivitas
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const colors = getLevelStatus(log.level);
                return (
                  <TableRow key={log.id}>
                    <TableCell>
                      {formatDate(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className="flex items-center gap-1.5 w-fit"
                        style={{ background: colors.bg, borderColor: colors.border, color: colors.text }}
                      >
                        {getLevelIcon(log.level)}
                        <span>{log.level}</span>
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-2xl">
                      <span className="break-words">{log.message}</span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View - Visible only on mobile */}
      <div className="md:hidden space-y-3">
        {logs.length === 0 ? (
          <Card>
            <CardContent className="text-center text-muted-foreground py-8">
              Tidak ada log aktivitas
            </CardContent>
          </Card>
        ) : (
          logs.map(log => renderMobileCard(log))
        )}
      </div>
    </>
  );
}